import { getApiBaseUrl } from '../services/api';

const PLATFORM_PORTAL_SUFFIXES = ['mts.homelab', 'mytownspotlight.dev', 'mytownspotlight.com'];
const RESERVED_PORTAL_SUBDOMAINS = new Set(['api', 'admin', 'app', 'app2', 'auth', 'portal', 'www']);

function normalizeListingId(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeHostname(value) {
  return String(value || '').split(':')[0].trim().toLowerCase();
}

function trimWww(hostname) {
  return String(hostname || '').replace(/^www\./i, '');
}

function buildPortalOriginFromHostname({ protocol, hostname }) {
  const configuredPortalBaseUrl = String(import.meta.env.VITE_PORTAL_BASE_URL || '').trim();
  if (configuredPortalBaseUrl) {
    return configuredPortalBaseUrl.replace(/\/+$/, '');
  }

  const normalizedHostname = normalizeHostname(hostname);
  if (!normalizedHostname || normalizedHostname === 'localhost' || normalizedHostname === '127.0.0.1') {
    return '';
  }

  if (normalizedHostname.startsWith('portal.')) {
    return `${protocol}//${normalizedHostname}`;
  }

  for (const suffix of PLATFORM_PORTAL_SUFFIXES) {
    if (normalizedHostname === suffix || normalizedHostname === `www.${suffix}`) {
      return `${protocol}//portal.${suffix}`;
    }

    if (normalizedHostname.endsWith(`.${suffix}`)) {
      const tenantPart = normalizedHostname.slice(0, -(suffix.length + 1));
      if (!tenantPart || RESERVED_PORTAL_SUBDOMAINS.has(tenantPart)) {
        return `${protocol}//portal.${suffix}`;
      }
      return `${protocol}//${tenantPart}.portal.${suffix}`;
    }
  }

  return `${protocol}//portal.${trimWww(normalizedHostname)}`;
}

export function buildPortalHref(path = '/') {
  const normalizedPath = `/${String(path || '/').replace(/^\/+/, '')}`.replace(/\/+$/, '') || '/';
  if (typeof window === 'undefined') {
    return normalizedPath;
  }

  const origin = buildPortalOriginFromHostname({
    protocol: window.location.protocol,
    hostname: window.location.hostname,
  });

  return origin ? `${origin}${normalizedPath}` : normalizedPath;
}

export function buildPortalHandoffHref(returnTo = '/dashboard', token = '') {
  const params = new URLSearchParams();
  const normalizedReturnTo = `/${String(returnTo || '/dashboard').replace(/^\/+/, '')}`;
  params.set('authHandoff', '1');
  params.set('returnTo', normalizedReturnTo);
  if (token) {
    params.set('token', token);
  }
  return buildPortalHref(`/?${params.toString()}`);
}

export function buildPortalHandoffConsumeHref(returnTo = '/dashboard', token = '', targetOrigin = '') {
  const normalizedReturnTo = `/${String(returnTo || '/dashboard').replace(/^\/+/, '')}`;
  const apiBaseUrl = getApiBaseUrl().replace(/\/+$/, '');
  const portalOrigin = String(targetOrigin || buildPortalHref('/')).replace(/\/+$/, '');
  const params = new URLSearchParams();
  params.set('token', token);
  params.set('returnTo', normalizedReturnTo);
  params.set('portalOrigin', portalOrigin);
  return `${apiBaseUrl}/api/auth/portal-handoff/consume?${params.toString()}`;
}

export function buildPortalListingHref(listingId, suffix = '') {
  const encodedListingId = encodeURIComponent(normalizeListingId(listingId));
  const normalizedSuffix = suffix ? `/${String(suffix).replace(/^\/+/, '')}` : '';
  return buildPortalHref(`/portal/listings/${encodedListingId}${normalizedSuffix}`);
}

async function generateOneTimePortalToken() {
  const apiBaseUrl = getApiBaseUrl();
  const response = await fetch(`${apiBaseUrl}/api/auth/one-time-token/generate`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  const payload = await response.json().catch(() => null);
  if (response.ok && payload?.token) {
    return payload.token;
  }

  const fallbackResponse = await fetch(`${apiBaseUrl}/api/auth/portal-handoff-token`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  const fallbackPayload = await fallbackResponse.json().catch(() => null);
  if (fallbackResponse.ok && fallbackPayload?.token) {
    return fallbackPayload.token;
  }

  const error = new Error(
    fallbackPayload?.message
      || fallbackPayload?.error
      || payload?.message
      || payload?.error
      || `Unable to prepare portal sign-in (${fallbackResponse.status || response.status})`,
  );
  error.status = fallbackResponse.status || response.status;
  throw error;
}

export async function buildPortalHandoffHrefForCurrentSession(returnTo = '/dashboard', providedToken = '', targetOrigin = '') {
  const token = providedToken || await generateOneTimePortalToken();
  return buildPortalHandoffConsumeHref(returnTo, token, targetOrigin);
}

function redirectToHref(navigate, href, options = {}) {
  if (/^https?:\/\//i.test(href)) {
    window.location.assign(href);
    return;
  }
  navigate(href, options);
}

export function redirectToPortalListing(navigate, listingId, suffix = '', options = {}) {
  const href = buildPortalListingHref(listingId, suffix);
  redirectToHref(navigate, href, options);
}

export function redirectToPortalDashboard(navigate, options = {}) {
  const href = buildPortalHref('/dashboard');
  redirectToHref(navigate, href, options);
}

export async function redirectToPortalWithHandoff(navigate, returnTo = '/dashboard', options = {}) {
  const { token, targetOrigin, ...navigationOptions } = options || {};
  try {
    const href = await buildPortalHandoffHrefForCurrentSession(returnTo, token, targetOrigin);
    redirectToHref(navigate, href, navigationOptions);
  } catch (error) {
    console.error('[portal.handoff] failed to prepare portal sign-in', {
      returnTo,
      status: error?.status,
      message: error?.message,
    });
    const fallbackHref = targetOrigin
      ? `${String(targetOrigin).replace(/\/+$/, '')}/${String(returnTo || '').replace(/^\/+/, '')}`
      : buildPortalHref(returnTo);
    redirectToHref(navigate, fallbackHref, navigationOptions);
  }
}
